/* 
 - TODO: Explanation
*/

import { DatabaseTable, returnID } from "../BaseTable";
import IntervalSet from "./IntervalSet";
import { types } from "../../helpers/Util";
import store from "../../store/Store";
import InteractionNode from "../interactions/InteractionNode";
import { runDatabaseFunction } from "../../helpers/HttpHelper";
import InteractionNodeInterval from "../interactions/InteractionNodeInterval";
import { navSliceActions } from "../../store/slices/NavSlice";

class IntervalRaw extends DatabaseTable {
  id: number;
  interval_set_id: number | null;
  name: string;
  start_day: number | null;
  end_day: number | null;
  communication_set_id: number | null;
  modified: Date;
  recurrence?: any;

  static readonly serializableFields = new Map([
    ["id", [types.number]],
    ["interval_set_id", [types.number, types.null]],
    ["name", [types.string]],
    ["start_day", [types.number, types.null]],
    ["end_day", [types.number, types.null]],
    ["communication_set_id", [types.number, types.null]],
    ["modified", [types.date, types.string]],
  ]);
  static readonly tableName = "Interval";

  constructor(
    id = -1,
    interval_set_id = null,
    name = "",
    start_day = null,
    end_day = null,
    communication_set_id = null,
    modified = new Date(),
    recurrence = {},
  ) {
    super();
    this.id = returnID(id);
    this.interval_set_id = interval_set_id;
    this.name = name;
    this.start_day = start_day;
    this.end_day = end_day;
    this.communication_set_id = communication_set_id;
    this.modified = modified;
    this.recurrence = recurrence;
  }
}

class Interval extends IntervalRaw {
  constructor(...args: any[]) {
    super(...args);
  }

  protected initialize() {
    const intervalSet = store
      .getState()
      .tables.tables.get(IntervalSet)
      ?.get(this.interval_set_id) as undefined | IntervalSet;
    if (typeof intervalSet !== "undefined") {
      intervalSet.update("intervals", intervalSet.intervals.push(this.id));
    }
    return super.initialize();
  }

  public static async addNewInterval(
    associatedInteractionNodes: Array<InteractionNode>,
    interval_set_id: number | null,
    name: string,
    start_day: number,
    end_day: number,
    communication_set_id: number | null,
  ) {
    // db update endpoint takes a comma-delimited string of intervalIDs
    // we need to extract them from associatedIntervals to formulate acceptable request param
    // i.e. '1,2,3'
    let interaction_node_ids = "";
    let counter = 0;
    associatedInteractionNodes.forEach((interactionNode) => {
      counter += 1;
      interaction_node_ids += interactionNode.id;
      // only add commas if we arent at the end
      if (counter !== associatedInteractionNodes.length) {
        interaction_node_ids += ",";
      }
    });

    let insertedInterval: Interval | null = null;
    let insertedInteractionNodeIntervals: InteractionNodeInterval[] | null =
      null;
    let insertedInteractionNodeInterval: InteractionNodeInterval | null = null; // for single interaction insert

    // if interval_set_id passed in, we are adding this to an interval set
    // and if we want to add to interval_set, we should use the db function
    // which handles everything at once
    if (interval_set_id) {
      // call the test.create_new_interaction_matrix_row postgres function to have postgres handle the following:
      // 1. create new interval in interval_set
      // 2. for each provided associated interaction_node, create a new interaction_node_interval record
      const params = {
        interval_set_id: interval_set_id,
        name: name,
        start_day: start_day,
        end_day: end_day,
        // associated_interaction_nodes: interaction_node_ids,
        associated_interaction_nodes: "", // no checked-off ini's by default
        communication_set_id: communication_set_id,
      };

      const dbFunc = "create_new_interaction_matrix_column";

      try {
        runDatabaseFunction(dbFunc, params).then(async (data: any) => {
          data = await data.text();
          data = JSON.parse(data);

          insertedInterval = data.interval;
          insertedInteractionNodeIntervals = data.interaction_node_intervals;

          if (insertedInterval) {
            const returnedInterval = await Interval.fromJSON(insertedInterval);
            insertedInteractionNodeIntervals?.forEach(
              (interactionNodeInterval: InteractionNodeInterval) => {
                InteractionNodeInterval.fromJSON(interactionNodeInterval);
              },
            );

            store.dispatch(
              navSliceActions.openRightDrawer({
                content: returnedInterval,
                type: "intervals",
                extraData: {
                  intervalSet: returnedInterval.interval_set_id,
                  intervalSetEditMode: true, // should this always be true? it was previously passed in from intervalSet editMode
                  new: true,
                },
              }),
            );
          }
        });
      } catch (err: any) {
        console.error("Error with adding interval to interval set: " + err);
      }

      // if no interval_set_id, just use basic immutability setters to
      // create the interval and the interaction_node_interval
      // and update local UI state
    } else {
      try {
        insertedInterval = await Interval.createInstance({
          interval_set_id: interval_set_id,
          name: name,
          start_day: start_day,
          end_day: end_day,
          communication_set_id: communication_set_id,
        }).save();
        insertedInteractionNodeInterval =
          await InteractionNodeInterval.createInstance({
            interaction_node_id: associatedInteractionNodes[0].id,
            interval_id: insertedInterval.id,
            condition_id: null,
            communication_set_id: null,
            target: "subject",
          }).save();

        // open the drawer to edit the newly inserted interval
        store.dispatch(
          navSliceActions.openRightDrawer({
            content: insertedInterval,
            type: "intervals",
          }),
        );
      } catch (err: any) {
        console.error(
          "Error with adding interval to individual interaction: " + err,
        );
      }
    }
  }
}

export default Interval;
